/**
 * Process Card On File Payment Actor
 *
 * Creates a PaymentIntent with a saved payment method and confirms it server-side.
 * Used when a returning user with a saved card wants to pay using that card.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getKioskId } from "../../lib/kiosk-identity";

/**
 * Input Schema (What this actor needs)
 */
const ProcessCardOnFilePaymentInputSchema = z.object({
  amount: z.number().positive(), // Amount in dollars (not cents)
  savedPaymentMethodId: z.string(), // The saved Stripe PM ID
  user: z
    .object({
      id: z.string(),
      fname: z.string(),
      lname: z.string(),
    })
    .nullable(),
  orgName: z.string(),
  consumer: z
    .object({
      id: z.string(),
      stripeCustomerId: z.string().optional().nullable(),
    })
    .optional(),
  creationSource: z.string().optional(),
  appointmentBtIds: z.array(z.string()).optional(),
  nonAppointmentBtIds: z.array(z.string()).optional(),
  collectedBy: z.string().optional(),
});

type ProcessCardOnFilePaymentInput = z.infer<
  typeof ProcessCardOnFilePaymentInputSchema
>;

/**
 * Success Schema (What this actor returns on success)
 */
const ProcessCardOnFilePaymentSuccessSchema = z.object({
  paymentIntentId: z.string(),
  success: z.boolean(),
});

type ProcessCardOnFilePaymentSuccess = z.infer<
  typeof ProcessCardOnFilePaymentSuccessSchema
>;

/**
 * Error Schema (What this actor returns on failure)
 */
const ProcessCardOnFilePaymentErrorSchema = z.object({
  code: z.enum([
    "INVALID_INPUT",
    "NETWORK_ERROR",
    "BACKEND_ERROR",
    "PAYMENT_FAILED",
    "UNKNOWN_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  details: z.unknown().optional(),
});

type ProcessCardOnFilePaymentError = z.infer<
  typeof ProcessCardOnFilePaymentErrorSchema
>;

/**
 * Actor metadata
 */
export interface ActorMetadata {
  displayName: string;
  description: string;
  category: "communication" | "data" | "auth" | "unknown";
  retryable: boolean;
  timeout: number;
  errorCodes: readonly string[];
  inputSchema: z.ZodSchema;
  successSchema: z.ZodSchema;
  errorSchema: z.ZodSchema;
  onSuccess: (result: unknown) => Record<string, unknown>;
  onError: (error: unknown) => Record<string, unknown>;
}

/**
 * Actor with metadata wrapper
 */
export interface ActorWithMetadata {
  // biome-ignore lint/suspicious/noExplicitAny: XState actor types are too complex for static typing
  impl: any;
  metadata: ActorMetadata;
}

/**
 * Custom error class for card on file payment
 */
export class CardOnFilePaymentError extends Error {
  code:
    | "NETWORK_ERROR"
    | "BACKEND_ERROR"
    | "PAYMENT_FAILED"
    | "INVALID_INPUT"
    | "UNKNOWN_ERROR";
  details?: unknown;
  retryable: boolean;
  actorName: string;
  functionName: string;
  location?: string;

  constructor(
    message: string,
    code:
      | "NETWORK_ERROR"
      | "BACKEND_ERROR"
      | "PAYMENT_FAILED"
      | "INVALID_INPUT"
      | "UNKNOWN_ERROR",
    details?: unknown,
    location?: string,
  ) {
    super(message);
    this.name = "CardOnFilePaymentError";
    this.code = code;
    this.details = details;
    this.retryable = code === "NETWORK_ERROR" || code === "BACKEND_ERROR";
    this.actorName = "processCardOnFilePayment";
    this.functionName = "processCardOnFilePayment";
    this.location = location;
  }
}

/**
 * Process Card On File Payment Actor Implementation
 *
 * Creates a PaymentIntent with the saved PM and confirms it server-side.
 */
const processCardOnFilePaymentActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: ProcessCardOnFilePaymentInput;
  }): Promise<ProcessCardOnFilePaymentSuccess> => {
    // Validate input
    const validated = ProcessCardOnFilePaymentInputSchema.safeParse(input);

    if (!validated.success) {
      const errorData: ProcessCardOnFilePaymentError = {
        code: "INVALID_INPUT",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, {
        ...errorData,
        actorName: "processCardOnFilePayment",
        location: "process-card-on-file-payment.actor.ts:validate",
      });
      throw error;
    }

    try {
      // Create and confirm PaymentIntent with saved payment method
      const response = await fetch("/api/stripe/process-card-on-file-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: validated.data.amount,
          paymentMethodId: validated.data.savedPaymentMethodId,
          user: validated.data.user,
          orgName: validated.data.orgName,
          consumer: validated.data.consumer,
          creationSource: validated.data.creationSource || "kiosk",
          appointmentBtIds: validated.data.appointmentBtIds,
          nonAppointmentBtIds: validated.data.nonAppointmentBtIds,
          collectedBy: validated.data.collectedBy || "kiosk",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new CardOnFilePaymentError(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`,
          "BACKEND_ERROR",
          errorData,
          "process-card-on-file-payment.actor.ts:fetch",
        );
      }

      const data = await response.json();

      if (!data.paymentIntentId) {
        throw new CardOnFilePaymentError(
          "Invalid response from payment API - missing paymentIntentId",
          "BACKEND_ERROR",
          data,
          "process-card-on-file-payment.actor.ts:response",
        );
      }

      datadogLogs.logger.info("[Fleet] Payment succeeded", {
        action: "consumer_payment_succeeded",
        metadata: {
          kioskId: getKioskId(),
          consumerId: validated.data.consumer?.id ?? null,
          paymentIntentId: data.paymentIntentId,
        },
      });

      return {
        paymentIntentId: data.paymentIntentId,
        success: true,
      };
    } catch (error) {
      if (error instanceof CardOnFilePaymentError) {
        const errorData: ProcessCardOnFilePaymentError = {
          code: error.code,
          message: error.message,
          retryable: error.retryable,
          details: error.details,
        };
        const err = new Error(errorData.message);
        Object.assign(err, {
          ...errorData,
          actorName: "processCardOnFilePayment",
          functionName: "processCardOnFilePayment",
          location: error.location,
        });
        datadogLogs.logger.error("[Fleet] Payment failed", {
          action: "consumer_payment_failed",
          metadata: {
            kioskId: getKioskId(),
            consumerId: validated.data.consumer?.id ?? null,
            paymentIntentId: null,
            errorCode:
              (error as CardOnFilePaymentError).code ?? "UNKNOWN_ERROR",
            errorMessage: (error as Error).message,
          },
        });
        throw err;
      }

      // Unknown error
      const errorData: ProcessCardOnFilePaymentError = {
        code: "UNKNOWN_ERROR",
        message: (error as Error).message || "Unknown error",
        retryable: true,
      };
      const err = new Error(errorData.message);
      Object.assign(err, {
        ...errorData,
        actorName: "processCardOnFilePayment",
        location: "process-card-on-file-payment.actor.ts:catch",
      });
      datadogLogs.logger.error("[Fleet] Payment failed", {
        action: "consumer_payment_failed",
        metadata: {
          kioskId: getKioskId(),
          consumerId: validated.data.consumer?.id ?? null,
          paymentIntentId: null,
          errorCode: (error as CardOnFilePaymentError).code ?? "UNKNOWN_ERROR",
          errorMessage: (error as Error).message,
        },
      });
      throw err;
    }
  },
);

/**
 * Process Card On File Payment Actor with Metadata
 */
export const processCardOnFilePaymentActor: ActorWithMetadata = {
  impl: processCardOnFilePaymentActorImpl,
  metadata: {
    displayName: "processCardOnFilePayment",
    description:
      "Process payment using saved card on file - creates and confirms PaymentIntent",
    category: "data",
    retryable: true,
    timeout: 30000, // 30 seconds
    errorCodes: [
      "INVALID_INPUT",
      "NETWORK_ERROR",
      "BACKEND_ERROR",
      "PAYMENT_FAILED",
      "UNKNOWN_ERROR",
    ],
    inputSchema: ProcessCardOnFilePaymentInputSchema,
    successSchema: ProcessCardOnFilePaymentSuccessSchema,
    errorSchema: ProcessCardOnFilePaymentErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as ProcessCardOnFilePaymentSuccess;
      return {
        paymentIntentId: success.paymentIntentId,
        paymentStatus: "success",
      };
    },
    onError: (error: unknown) => {
      const err = error as ProcessCardOnFilePaymentError;
      return {
        paymentStatus: "failure",
        paymentError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
